// 04 · Señales: activas (ENTRAR, borde verde), Vigilar (umbral / falta),
// Handball, En análisis (.lv.full con edge actual: la pantalla nunca queda
// vacía con partidos en vivo), Emitidas hoy (📡 RADAR o deporte + resultado).
// 15.2 vacío con próximos partidos · 16.3 skeleton + toast de error.
// Umbral real por deporte: básquet ±10 · handball +3 / −2 (decisión 2).
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../../auth/AuthContext';
import useClientStats from '../../hooks/useClientStats';
import useRadarResults, { santiagoDayRadar } from '../../../hooks/useRadarResults';
import { santiagoDateStr } from '../../../components/PerformanceLab';
import { matchOf, pickOf } from '../../components/SignalCard';
import { LvCard, SigCard, NoAccess } from '../cards';
import { EmptyInline, EmptyFull, NextUp, OffBar, SigSkeleton, useToast } from '../ui';
import { useTodayGames, useOnline, ventanaDe } from '../hooks';
import { useLiveV3, fmtNum, pickLive, gidDeSenal } from '../live';

const esHbSig = (s) => s.sport === 'handball' || s.match_type === 'handball' || /handball|hbl|ehf|asobal|starligue|handbold|handboll/i.test(String(s.league || s.liga || ''));
const resOf = (r) => (r === 'WIN' ? { v: 'WIN', res: 'w' } : r === 'LOSS' ? { v: 'LOSS', res: 'l' } : r === 'PUSH' ? { v: 'PUSH', res: 'p' } : { v: 'En juego', res: 'p' });

export default function Senales() {
  const navigate = useNavigate();
  const { search } = useLocation();
  const toast = useToast();
  const { hasActiveAccess, isAdmin } = useAuth();
  const acceso = hasActiveAccess || isAdmin;
  const { live, loading: lLive } = useLiveV3();
  const { hoy, loading: lSig, error } = useClientStats();
  const { alerts } = useRadarResults();
  const { games: agenda } = useTodayGames(santiagoDateStr(new Date()));
  const { online, sinceStr } = useOnline();
  const [sport, setSport] = useState('todas'); // todas | basquet | handball | edge
  const [q, setQ] = useState(null);           // null | 1..4
  const emitRef = useRef(null);

  try { sessionStorage.setItem('ee-app-signals-seen', String(Date.now())); } catch (_e) { /* noop */ }
  useEffect(() => { if (error) toast('No pudimos actualizar las señales', { action: 'Reintentar', onAction: () => window.location.reload() }); }, [error, toast]);
  // "Señales enviadas hoy" (desde Inicio): abre esta pestaña en Emitidas hoy
  const verHoy = new URLSearchParams(search).get('ver') === 'hoy';
  useEffect(() => { if (verHoy && !lSig && emitRef.current) emitRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' }); }, [verHoy, lSig]);

  const filtrados = useMemo(() => {
    let l = live.slice();
    if (sport === 'basquet') l = l.filter((x) => !x.esHb);
    if (sport === 'handball') l = l.filter((x) => x.esHb);
    if (sport === 'edge') l.sort((a, b) => Math.abs(b.edge || 0) - Math.abs(a.edge || 0));
    if (q) l = l.filter((x) => x.periodo === q);
    return l;
  }, [live, sport, q]);
  const activas = filtrados.filter((l) => l.estado === 'ENTRAR');
  const vigilar = filtrados.filter((l) => l.estado === 'VIGILAR');
  const hb = filtrados.filter((l) => l.esHb && l.estado !== 'ENTRAR' && l.estado !== 'VIGILAR');
  const analisis = filtrados.filter((l) => !l.esHb && l.estado !== 'ENTRAR' && l.estado !== 'VIGILAR');
  const radarHoy = useMemo(() => alerts.filter((a) => a.fecha === santiagoDayRadar(new Date())), [alerts]);
  const emitidas = useMemo(() => [
    ...hoy.filter((s) => sport === 'todas' || sport === 'edge' || (sport === 'handball') === esHbSig(s)).map((s) => ({ key: `s_${s.id}`, ts: s.started_at || 0, tipo: 'live', s })),
    ...(sport === 'handball' ? [] : radarHoy).map((a) => ({ key: `r_${a.id}`, ts: a.ts, tipo: 'radar', a })),
  ].sort((x, y) => y.ts - x.ts), [hoy, radarHoy, sport]);
  const proximos = useMemo(() => agenda.filter((g) => g.estado === 'proximo' && g.cobertura), [agenda]);
  const proxHb = proximos.find((g) => g.esHandball);

  const gate = (list) => (acceso ? list : list.slice(0, 2));
  const nActivas = live.filter((l) => l.estado === 'ENTRAR').length;
  const Chip = ({ id, cur, set, children }) => (
    <button type="button" className={`v3chip${cur === id ? ' on' : ''}`} onClick={() => set(cur === id ? (typeof id === 'number' ? null : 'todas') : id)}>{children}</button>
  );

  const cargando = lLive || lSig;
  const vacio = !cargando && live.length === 0;

  return (
    <div className="v3"><div className="scroll safe-top" style={vacio ? { display: 'flex', flexDirection: 'column' } : undefined}>
      <div className="pgh"><h1>Señales</h1><p>{cargando ? 'Cargando…' : `${nActivas} activa${nActivas === 1 ? '' : 's'} ahora · ${live.length} partido${live.length === 1 ? '' : 's'} en análisis.`}</p></div>
      {!online && <OffBar since={sinceStr} />}
      <div className="chips">
        <Chip id="todas" cur={sport} set={setSport}>Todas</Chip><Chip id="basquet" cur={sport} set={setSport}>Básquet</Chip>
        <Chip id="handball" cur={sport} set={setSport}>Handball</Chip><Chip id="edge" cur={sport} set={setSport}>Mayor edge</Chip>
      </div>
      {!vacio && <div className="chips" style={{ paddingTop: 0 }}>{[1, 2, 3, 4].map((n) => <Chip key={n} id={n} cur={q} set={setQ}>Q{n}</Chip>)}</div>}

      {cargando && (<><p className="grp">Básquet</p><SigSkeleton /><SigSkeleton /><p className="grp">Handball</p><SigSkeleton /></>)}

      {vacio && (
        <EmptyFull icon={<svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#1D1D1F" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 17l5-6 4 4 5-8 4 5" /></svg>}
          title="El motor está escaneando"
          extra={proximos.slice(0, 2).map((g, i) => <NextUp key={g.gid} emoji={g.esHandball ? '🤾' : '🏀'} liga={g.liga} partido={`${g.home} vs ${g.away}`} hora={g.hora} style={i ? { marginTop: 8 } : undefined} />)}>
          No hay partidos en vivo con cobertura en este momento. Las señales aparecen aquí en cuanto se emiten.
        </EmptyFull>
      )}

      {!cargando && !vacio && (
        <div className={online ? '' : 'stale'}>
          {activas.length > 0 && (
            <>
              <p className="grp row">Activas · {activas.length}<span className="lvb"><i />Live</span></p>
              {gate(activas).map((l) => (
                <SigCard key={l.gid} live liga={l.liga} chip={{ kind: 'stt', cls: 'g', txt: 'Entrar' }} edge={l.edge} away={l.away} home={l.home}
                  cols={[{ l: 'Pick', v: pickLive(l), cls: 'g' }, { l: 'Ventana', v: l.tiempo }, { l: 'Proyección', v: l.esHb ? l.proyeccion.toFixed(1) : Math.round(l.proyeccion) }]}
                  onClick={() => navigate(`/app/partido/${l.gid}`)} />
              ))}
            </>
          )}
          {vigilar.length > 0 && (
            <>
              <p className="grp">Vigilar · {vigilar.length}</p>
              {gate(vigilar).map((l) => (
                <SigCard key={l.gid} liga={l.liga} chip={{ kind: 'stt', cls: 'w', txt: 'Vigilar' }} edge={l.edge} away={l.away} home={l.home}
                  cols={[{ l: 'Tendencia', v: l.lado || '—' }, { l: 'Umbral', v: fmtNum(l.umbral) }, { l: 'Falta', v: l.falta.toFixed(1), cls: 'wn' }]}
                  onClick={() => navigate(`/app/partido/${l.gid}`)} />
              ))}
            </>
          )}
          {sport !== 'basquet' && (
            <>
              <p className="grp">Handball · {hb.length}</p>
              {hb.length === 0 ? (
                <EmptyInline title="Sin señales de handball">
                  {proxHb ? `Próximo partido con cobertura: ${proxHb.home} vs ${proxHb.away}, ${proxHb.hora}.` : 'El motor las emite en la segunda mitad, cuando el edge supera el umbral.'}
                </EmptyInline>
              ) : gate(hb).map((l) => <LvCard key={l.gid} l={l} full />)}
            </>
          )}
          {analisis.length > 0 && (
            <>
              <p className="grp">En análisis · {analisis.length}</p>
              {gate(analisis).map((l) => <LvCard key={l.gid} l={l} full />)}
            </>
          )}
          {!acceso && filtrados.length > 2 && <NoAccess que="Solo ves los primeros partidos de cada sección." />}
        </div>
      )}

      {!cargando && (
        <div ref={emitRef}>
          <p className="grp">Emitidas hoy · {emitidas.length}</p>
          {emitidas.length === 0 ? <EmptyInline title="Sin señales emitidas hoy">El motor solo emite cuando el edge supera el umbral. Te avisamos en cuanto salga la primera.</EmptyInline>
            : gate(emitidas).map((x) => x.tipo === 'live' ? (
              <SigCard key={x.key} liga={x.s.league || x.s.liga || '—'} chip={{ kind: 'sport', txt: esHbSig(x.s) ? '🤾 Handball' : '🏀 Básquet' }} edge={x.s.edge_initial ?? x.s.edge}
                away={matchOf(x.s).split(' vs ')[0]} home={matchOf(x.s).split(' vs ')[1] || ''}
                cols={[{ l: 'Pick', v: pickOf(x.s), cls: 'g' }, { l: 'Ventana', v: ventanaDe(x.s) }, { l: 'Resultado', ...resOf(x.s.result) }]}
                onClick={() => navigate(`/app/partido/${gidDeSenal(x.s)}`, { state: { signal: x.s } })} />
            ) : (
              <SigCard key={x.key} liga={x.a.liga} chip={{ kind: 'sport', txt: '📡 RADAR' }} edge={x.a.edge} away={x.a.partido.split(' @ ')[0]} home={x.a.partido.split(' @ ')[1] || ''}
                cols={[{ l: 'Pick', v: `${x.a.side} ${x.a.linea ?? ''}`, cls: 'g' }, { l: 'Ventana', v: x.a.ventana }, { l: 'Resultado', ...(x.a.resolved ? (x.a.won ? { v: 'WIN', res: 'w' } : { v: 'LOSS', res: 'l' }) : { v: 'En juego', res: 'p' }) }]}
                onClick={() => navigate(`/app/partido/${x.a.gid}`)} />
            ))}
          {!acceso && emitidas.length > 2 && <NoAccess que={`Hay ${emitidas.length - 2} señal${emitidas.length - 2 === 1 ? '' : 'es'} más emitida${emitidas.length - 2 === 1 ? '' : 's'} hoy.`} />}
        </div>
      )}
    </div></div>
  );
}
